import PropTypes from 'prop-types';
import React from 'react';
import { useRouter } from 'next/router';

/** Hooks */
import useAuth from 'hooks/useAuth';
import { useCategoryData } from 'hooks/useCategoryData';

/* Layout */
import FullWidthLayout from 'layouts/FullWidthLayout';

/** Material-UI */
import { Box, useTheme } from '@mui/material';

/** Constants */
import { DEFAULT_TAB_LISTING_PAGES } from 'constants/listing-defaults';

/** Utils */
import { productCarouselSettings } from 'utils/carousels';

/** Components */
import CBreadcrumbs from 'components/CBreadcrumbs';
import CategoryPageSeo from 'components/Seo/CategoryPageSeo';
import CategoryDescription from 'components/Category/CategoryDescription';
import CategoryChildrenList from 'components/Category/CategoryChildrenList';
import AlgoliaResultsLayout from 'components/common/AlgoliaResultsLayout/AlgoliaResultsLayout';
import RecentBrowsedBrandsCarousel from 'components/Carousels/common/RecentBrowsed/RecentBrowsedBrandsCarousel';
import RecentBrowsedProductsCarousel from 'components/Carousels/common/RecentBrowsed/RecentBrowsedProductsCarousel';

/** Sections */
import SignUpCTA from '../../sections/ctas/SignUpCTA';

const categoryLayoutDefaultPropCategoryData = {
  slug: '',
  categoryData: {
    name: '',
    slug: '',
    description: '',
    shortDescription: '',
    seoTitle: '',
    seoDescription: '',
    parent: {},
    children: [],
  },
};

const categoryLayoutDefaultPropInitialAlgoliaData = {
  filters: [],
  banners: [],
  products: [],
  totalProducts: 0,
  brands: [],
  totalBrands: 0,
};

const categoryLayoutDefaultPropInitialBreadcrumbs = [];

const CategoryLayout = ({
  slug,
  initialTab = DEFAULT_TAB_LISTING_PAGES,
  categoryData: initialCategoryData = categoryLayoutDefaultPropCategoryData,
  initialBreadcrumbs = categoryLayoutDefaultPropInitialBreadcrumbs,
  initialAlgoliaData = categoryLayoutDefaultPropInitialAlgoliaData,
}) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const { query, asPath, pathname } = router;

  const { categoryData: currentCategoryData, isLoading } = useCategoryData(
    { categoryData: initialCategoryData, initialBreadcrumbs },
    query.slug
  );

  // Use the current category data or fall back to initial data
  const displayData = isLoading ? { categoryData: initialCategoryData, initialBreadcrumbs } : currentCategoryData;

  // eslint-disable-next-line no-unused-vars
  const baseUrl = `https://www.creoate.com`;
  const currentTab = query?.tab || initialTab;

  const handleBreadcrumbClick = (breadcrumb) => {
    if (breadcrumb.slug === '/') {
      router.push(`/`);
    } else if (router.query.slug !== breadcrumb.slug) {
      router.push(`/product-category/${breadcrumb.slug}`);
    }
  };

  return (
    <>
      <CategoryPageSeo
        category={displayData.categoryData}
        url={baseUrl + asPath}
        canonical={baseUrl + pathname}
        seoTitle={displayData.categoryData.seoTitle}
        seoDescription={displayData.categoryData.seoDescription}
        products={initialAlgoliaData?.products?.slice(0, 5)}
      />
      <CBreadcrumbs
        sx={{ py: 0.5, px: { xs: 0, md: 4, lg: 2, xl: 3 } }}
        onBreadcrumbClick={handleBreadcrumbClick}
        initialBreadcrumbs={displayData.initialBreadcrumbs}
      />
      <CategoryChildrenList category={displayData.categoryData} />
      <AlgoliaResultsLayout
        reference="product-category"
        searchQuery={slug}
        initialAlgoliaData={initialAlgoliaData}
        key={`${slug}-results-layout`}
        initialTab={currentTab}
      />
      <Box
        sx={{
          py: 0.5,
          px: { xs: 0, md: 4, lg: 2, xl: 3 },
          [theme.breakpoints.down('md')]: {
            pt: 1,
          },
        }}
      >
        {currentTab === 'products' && (
          <>
            <RecentBrowsedProductsCarousel
              type="products"
              viewAllRef="plp"
              sx={{ mt: { xs: 1, md: 3 } }}
              carouselSettings={productCarouselSettings}
            />
            <RecentBrowsedBrandsCarousel
              type="products"
              viewAllRef="plp"
              sx={{ mt: { xs: 1, md: 3 } }}
              carouselSettings={productCarouselSettings}
            />
          </>
        )}
        {currentTab === 'brands' && (
          <>
            <RecentBrowsedBrandsCarousel
              type="brands"
              viewAllRef="plp"
              sx={{ mt: { xs: 1, md: 3 } }}
              carouselSettings={productCarouselSettings}
            />
            <RecentBrowsedProductsCarousel
              type="brands"
              viewAllRef="plp"
              sx={{ mt: { xs: 1, md: 3 } }}
              carouselSettings={productCarouselSettings}
            />
          </>
        )}
      </Box>
      {!isAuthenticated && <SignUpCTA />}
      <CategoryDescription category={displayData.categoryData} sx={{ pl: 3, my: 3.75 }} />
    </>
  );
};

CategoryLayout.getLayout = FullWidthLayout;

CategoryLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  initialTab: PropTypes.string,
  categoryData: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    shortDescription: PropTypes.string,
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    parent: PropTypes.shape({}),
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialBreadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
  initialAlgoliaData: PropTypes.shape({
    filters: PropTypes.arrayOf(PropTypes.shape({})),
    banners: PropTypes.arrayOf(PropTypes.shape({})),
    products: PropTypes.arrayOf(PropTypes.shape({})),
    totalProducts: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
    totalBrands: PropTypes.number,
  }),
};

export default CategoryLayout;
